"use client";

import { cn } from "@/lib/utils";
import { GradientSurface, GradientVariant } from "@/components/effects";

export type OverlayVariant =
  | "bottom-fade"
  | "top-fade"
  | "full"
  | "subtle"
  | "none";
export type OverlayPosition = "top" | "bottom" | "full";

export interface ImageHeroProps {
  /** Image source URL */
  src?: string | null;
  /** Alt text for the image */
  alt?: string;
  /** Fallback gradient variant when no image is provided */
  fallbackVariant?: GradientVariant;
  /** Height class (Tailwind) - e.g., "h-48", "h-80" */
  height?: string;
  /** Additional CSS classes */
  className?: string;
  /** Child elements (overlays, badges, content) */
  children?: React.ReactNode;
  /** Whether image zooms on hover */
  zoomOnHover?: boolean;
  /** Whether to show decorative pattern on fallback gradient */
  showPattern?: boolean;
}

/**
 * ImageHero - Hero image section with fallback gradient
 *
 * Displays an image that zooms on hover, or falls back to a gradient
 * when no image is provided. Children are positioned absolutely.
 *
 * @example
 * <ImageHero
 *   src={league.imageUrl}
 *   fallbackVariant="emerald"
 *   height="h-80"
 * >
 *   <BadgeStack position="top-left">...</BadgeStack>
 *   <ImageOverlay variant="bottom-fade" position="bottom">
 *     <h2>Title</h2>
 *   </ImageOverlay>
 * </ImageHero>
 */
export function ImageHero({
  src,
  alt = "",
  fallbackVariant = "emerald",
  height = "h-48",
  className,
  children,
  zoomOnHover = true,
  showPattern = true,
}: ImageHeroProps) {
  const hasImage = src && src.length > 0;

  return (
    <div className={cn("relative overflow-hidden", height, className)}>
      {/* Background */}
      {hasImage ? (
        <img
          src={src}
          alt={alt}
          className={cn(
            "absolute inset-0 w-full h-full object-cover",
            zoomOnHover &&
              "transition-transform duration-700 group-hover:scale-110"
          )}
        />
      ) : (
        <GradientSurface
          variant={fallbackVariant}
          pattern={showPattern ? "plus" : "none"}
          patternOpacity={10}
          className="absolute inset-0"
        />
      )}

      {/* Children (overlays, badges, etc.) */}
      {children}
    </div>
  );
}

export interface ImageOverlayProps {
  /** Type of gradient overlay */
  variant: OverlayVariant;
  /** Where to position child content */
  position?: OverlayPosition;
  /** Additional CSS classes */
  className?: string;
  /** Child content */
  children?: React.ReactNode;
}

const overlayClasses: Record<Exclude<OverlayVariant, "none">, string> = {
  "bottom-fade": "bg-gradient-to-t from-black/80 via-black/20 to-transparent",
  "top-fade": "bg-gradient-to-b from-black/60 via-transparent to-black/20",
  full: "bg-gradient-to-b from-black/40 to-black/60",
  subtle: "bg-gradient-to-t from-black/60 via-transparent to-transparent",
};

const positionClasses: Record<OverlayPosition, string> = {
  top: "items-start pt-4",
  bottom: "items-end pb-4",
  full: "items-end pb-4",
};

/**
 * ImageOverlay - Gradient overlay for image content legibility
 *
 * Adds gradient overlays on top of images to ensure text remains readable.
 * Position prop controls where children are rendered within the overlay.
 *
 * @example
 * <ImageHero src={imageUrl}>
 *   <ImageOverlay variant="bottom-fade" position="bottom">
 *     <h2 className="text-white">Title on dark overlay</h2>
 *     <p className="text-gray-200">Subtitle text</p>
 *   </ImageOverlay>
 * </ImageHero>
 */
export function ImageOverlay({
  variant,
  position = "bottom",
  className,
  children,
}: ImageOverlayProps) {
  if (variant === "none") {
    return (
      <div
        className={cn(
          "absolute inset-0 flex flex-col",
          positionClasses[position],
          className
        )}
      >
        <div className="px-4 w-full">{children}</div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "absolute inset-0 flex flex-col",
        overlayClasses[variant],
        positionClasses[position],
        className
      )}
    >
      <div className="px-4 w-full text-white">{children}</div>
    </div>
  );
}

export default ImageHero;
